import { MonoTypeOperatorFunction, tap } from 'rxjs';

/**
 * Custom RxJS operator that scrolls the window to the top after data loads.
 *
 * This operator is designed to work with data-loading observables (like API calls)
 * where you want to ensure the page is scrolled to the top AFTER the data arrives
 * and content renders, rather than when the route changes (which happens before
 * spinners disappear).
 *
 * Use this in conjunction with Angular Router's `scrollPositionRestoration: 'top'`
 * setting. The router config handles immediate scroll on route change, while this
 * operator ensures the final scroll position is correct after async data loads.
 *
 * @example
 * ```typescript
 * syncPeople(request: PeopleSyncRequest): Observable<PeopleSyncResponse> {
 *   return this.httpService
 *     .doPostRequest<PeopleSyncResponse>(url, request)
 *     .pipe(
 *       scrollToTopAfterData(),
 *       // other operators...
 *     );
 * }
 * ```
 *
 * @returns An RxJS operator that scrolls to top when data flows through
 */
export function scrollToTopAfterData<T>(): MonoTypeOperatorFunction<T> {
  return tap(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  });
}
